
import React from 'react';
import { generateImage } from '../../../utils/mediaGen';
import { Image as ImageIcon, Sparkles, Volume2, BookOpen, Sigma, Lightbulb } from 'lucide-react';

interface ContentWidgetProps {
    data: any;
    subject: string;
    nodeId: string;
    isDark: boolean;
    onPlayAudio?: (text: string) => void;
}

export const VisualImageWidget: React.FC<ContentWidgetProps> = ({ data, subject, nodeId, isDark }) => (
    <div className={`relative w-full h-full group min-h-[400px] flex items-center justify-center overflow-hidden rounded-[2.5rem] ${isDark ? 'bg-black/20' : 'bg-slate-100/50'}`}>
        <img
            src={generateImage(data.desc || data.caption, nodeId, subject)}
            alt="Visual"
            className="absolute inset-0 w-full h-full object-cover opacity-90 transition-opacity duration-700 hover:scale-105"
            loading="eager"
            onError={(e) => {
                (e.target as HTMLImageElement).src = "https://placehold.co/1024x1024/1e1b4b/FFFFFF?text=Image+Generating...";
            }}
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-black/80 via-transparent' : 'from-slate-900/60 via-transparent'} to-transparent opacity-90`}></div>
        <div className="absolute bottom-0 left-0 w-full p-10 md:p-14">
            <p className="text-2xl md:text-3xl font-black text-white leading-tight drop-shadow-lg">
                {data.caption}
            </p>
        </div>
    </div>
);

export const ComicStripWidget: React.FC<ContentWidgetProps> = ({ data, subject, nodeId, isDark }) => (
    <div className={`flex flex-col h-full ${isDark ? 'text-white' : 'text-slate-800'}`}>
        <div className="flex-1 overflow-x-auto p-6 flex items-center gap-6 snap-x snap-mandatory scrollbar-hide">
            {data.panels?.map((panel: any, i: number) => (
                <div key={i} className="snap-center shrink-0 w-72 md:w-96 bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-2xl border-4 border-white dark:border-white/10 transform hover:scale-[1.01] transition-transform flex flex-col">
                    <img
                        src={generateImage(`cartoon style ${panel.desc}`, `${nodeId}_comic_${i}`, subject)}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                    />
                    <div className="p-6 flex-1 flex items-center">
                        <p className="font-bold text-slate-800 dark:text-white text-lg leading-snug">"{panel.speech}"</p>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export const VocabCardWidget: React.FC<ContentWidgetProps> = ({ data, isDark, onPlayAudio }) => {
    const textPrimary = isDark ? 'text-white' : 'text-slate-900';
    return (
        <div className="flex flex-col items-center justify-center p-4 w-full h-full relative" onClick={() => onPlayAudio && onPlayAudio(data.word)}>
            <div className={`backdrop-blur-lg border p-12 rounded-[3.5rem] text-center w-full max-w-md shadow-2xl transition-all hover:scale-[1.02] ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-100 hover:shadow-indigo-100'}`}>
                <span className={`inline-block px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-10 border ${isDark ? 'bg-white/10 text-indigo-200 border-white/10' : 'bg-indigo-50 text-indigo-600 border-indigo-100'}`}>New Discovery</span>
                <h2 className={`text-6xl md:text-7xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{data.word}</h2>
                <p className={`text-2xl font-mono mb-12 opacity-80 ${isDark ? 'text-indigo-300' : 'text-indigo-500'}`}>/{data.pronunciation}/</p>
                <div className={`p-8 rounded-[2rem] border ${isDark ? 'bg-black/30 border-white/5' : 'bg-slate-50 border-slate-100'}`}>
                    <p className={`text-2xl font-bold leading-relaxed ${textPrimary}`}>{data.meaning}</p>
                </div>
                <div className="mt-8 text-indigo-400 animate-bounce"><Volume2 size={48} /></div>
            </div>
        </div>
    );
};

export const TextWidget: React.FC<ContentWidgetProps> = ({ data, isDark }) => (
    <div className="px-6 py-8 md:px-12 flex items-center justify-center h-full">
        <div className={`p-12 md:p-16 rounded-[3rem] border shadow-sm max-w-3xl w-full text-center ${isDark ? 'bg-slate-900/60 border-white/10' : 'bg-slate-50/50 border-slate-100 shadow-slate-200/50'}`}>
            <div className={`text-2xl md:text-3xl font-bold leading-relaxed italic ${isDark ? 'text-indigo-50' : 'text-slate-800'}`}>
                “{typeof data === 'string' ? data : JSON.stringify(data)}”
            </div>
        </div>
    </div>
);

export const FormulaWidget: React.FC<ContentWidgetProps> = ({ data, isDark }) => (
    <div className="p-8 flex items-center justify-center h-full">
        <div className={`p-16 rounded-[4rem] border shadow-2xl max-w-3xl w-full text-center relative overflow-hidden ${isDark ? 'bg-indigo-950/60 border-indigo-500/30' : 'bg-white border-slate-200'}`}>
            <div className={`text-4xl md:text-6xl font-black font-orbitron leading-relaxed drop-shadow-lg ${isDark ? 'text-white' : 'text-indigo-900'}`}>
                {data}
            </div>
        </div>
    </div>
);

export const DidYouKnowWidget: React.FC<ContentWidgetProps> = ({ data, isDark }) => {
    const textPrimary = isDark ? 'text-white' : 'text-slate-900';
    return (
        <div className="p-8 flex items-center justify-center h-full">
            <div className={`p-12 rounded-[3.5rem] border shadow-2xl max-w-md w-full relative ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-100'}`}>
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-amber-500 text-white p-5 rounded-full border-4 border-white shadow-xl animate-bounce">
                    <Lightbulb size={48} className="fill-white" />
                </div>
                <div className="mt-8 text-center">
                    <h3 className="text-amber-500 font-black uppercase tracking-widest text-xs mb-8">Fun Fact</h3>
                    <p className={`text-2xl font-bold leading-relaxed ${textPrimary}`}>
                        {data}
                    </p>
                </div>
            </div>
        </div>
    );
};
